import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, User, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const [stage, setStage] = useState('credentials');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [otp, setOtp] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(credentials.username, credentials.password);
    setLoading(false);
    if (result.requiresOtp) {
      setTempToken(result.tempToken);
      setStage('otp');
    } else if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(null, null, otp, tempToken);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleResendOtp = async () => {
    if (!tempToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to resend OTP');
      alert('OTP sent again to your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">
            <LogIn size={32} color="#3b82f6" />
          </div>
          <h1 className="login-title">PlayerMaker Inventory</h1>
          <p className="login-subtitle">Sign in to manage your inventory</p>
        </div>
        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            <span className="error-text">{error}</span>
          </div>
        )}
        {stage === 'credentials' && (
          <form onSubmit={handleSubmitCredentials}>
            <div className="form-group">
              <label htmlFor="username" className="form-label">Username</label>
              <div className="input-wrapper">
                <User className="input-icon" size={20} />
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={credentials.username}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={credentials.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        )}
        {stage === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <div className="form-group">
              <label htmlFor="otp" className="form-label">Enter the OTP sent to your email</label>
              <input
                id="otp"
                name="otp"
                type="text"
                required
                maxLength="6"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="form-input"
                placeholder="6-digit OTP"
                disabled={loading}
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </button>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              className="btn-secondary"
              style={{ marginTop: '10px' }}
            >
              Resend OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
